import axios, { AxiosInstance } from 'axios'
import API_CONFIG from '../config/api.config'
import authRepository from './auth.repository'
// @ts-ignore - Import traders app store for token access
import store from '@/store'

export interface ClubRanking {
    clubId: number
    clubName: string
    clubImageUrl: string | null
    clubPrimaryColor: string
    totalVolume: number
    memberCount: number
    rank: number
}

export interface LeagueOverview {
    leaderboardId: number
    leaderboardName: string
    partnerId: number
    partnerName: string
    assessmentType: string
    startTime: string
    endTime: string
    secsAutoRefresh: number | null
    totalClubs: number
    totalVolume: number
    clubRanking: ClubRanking[]
}

class LeaguesRepository {
    private api: AxiosInstance

    constructor() {
        this.api = axios.create({
            baseURL: API_CONFIG.BASE_URL,
            timeout: API_CONFIG.TIMEOUT,
            headers: {
                'Content-Type': 'application/json'
            }
        })

        // Request interceptor to add auth token
        this.api.interceptors.request.use(
            config => {
                // Try to get token from traders app store first (already includes 'Bearer ' prefix)
                let token = store?.state?.token

                // If no traders token, try broker auth
                if (!token) {
                    const brokerToken = authRepository.getToken()
                    if (brokerToken) {
                        token = `Bearer ${brokerToken}`
                    }
                }

                // Fallback to broker getter if available
                if (!token && store?.getters?.['broker/authToken']) {
                    token = `Bearer ${store.getters['broker/authToken']}`
                }

                if (token) {
                    config.headers.Authorization = token
                }
                return config
            },
            error => Promise.reject(error)
        )

        // Response interceptor for error handling
        this.api.interceptors.response.use(
            response => response,
            error => {
                if (error.response?.status === 401) {
                    // Check if token exists in either traders store or broker auth
                    const tradersToken = store?.state?.token
                    const brokerToken = authRepository.getToken()

                    // Only redirect if user had a token (not for public access)
                    if (tradersToken) {
                        // Token expired in traders app - redirect to traders login
                        // The store is persisted, so removing from localStorage will clear it
                        localStorage.removeItem('token')
                        window.location.href = '/'
                    } else if (brokerToken) {
                        // Token expired in broker app - redirect to broker login
                        authRepository.clearAuthData()
                        window.location.href = '/broker/login'
                    }
                    // If no token existed, this was public access - don't redirect
                }
                return Promise.reject(error)
            }
        )
    }

    /**
     * Get league overview for a specific partner and league type
     * @param partnerId - The ID of the partner
     * @param assessmentType - Type of assessment (IBS_LEAGUE, IBS_MEMBERS_LEAGUE, etc.)
     * @param leaderboardType - Type of leaderboard (GOLD, SILVER, BRONZE, DIAMOND)
     * @returns Promise with the league overview
     */
    async getLeagueOverview(
        partnerId: number,
        assessmentType: string,
        leaderboardType: string
    ): Promise<LeagueOverview> {
        // Check if user is demo@swiset.com and return mock data
        const isDemoUser = this.isDemoUser()
        if (isDemoUser) {
            return this.getMockLeagueData(partnerId, assessmentType, leaderboardType)
        }

        try {
            const response = await this.api.get<LeagueOverview>(
                API_CONFIG.LEAGUES.OVERVIEW(partnerId, assessmentType, leaderboardType)
            )
            return response.data
        } catch (error: any) {
            if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.error('Error fetching league overview:', error?.message || error)
            }
            throw error
        }
    }

    /**
     * Check if current user is the demo user
     */
    private isDemoUser(): boolean {
        try {
            // Check broker store
            const brokerUser = localStorage.getItem('broker_user')
            if (brokerUser) {
                const userData = JSON.parse(brokerUser)
                if (userData.username === 'demo@swiset.com' || userData.email === 'demo@swiset.com') {
                    return true
                }
            }

            // Check if user email is in store (for broker app)
            // @ts-ignore
            const storeUser = store?.state?.broker?.user
            if (storeUser && (storeUser.email === 'demo@swiset.com' || storeUser.username === 'demo@swiset.com')) {
                return true
            }

            return false
        } catch (error) {
            return false
        }
    }

    /**
     * Generate mock league data for demo purposes
     */
    private getMockLeagueData(partnerId: number, assessmentType: string, leaderboardType: string): LeagueOverview {
        const tierNames = {
            'DIAMOND': 'Platinum',
            'GOLD': 'Gold',
            'SILVER': 'Silver',
            'BRONZE': 'Bronze'
        }

        const tierName = tierNames[leaderboardType as keyof typeof tierNames] || leaderboardType

        // Generate mock club rankings based on tier
        const mockClubs = this.generateMockClubs(leaderboardType)

        return {
            leaderboardId: Math.floor(Math.random() * 1000),
            leaderboardName: `${tierName} League - Demo`,
            partnerId: partnerId,
            partnerName: 'Swiset Demo',
            assessmentType: assessmentType,
            startTime: '2026-01-01T00:00:00Z',
            endTime: '2026-12-31T23:59:59Z',
            secsAutoRefresh: 300,
            totalClubs: mockClubs.length,
            totalVolume: mockClubs.reduce((sum, club) => sum + club.totalVolume, 0),
            clubRanking: mockClubs
        }
    }

    /**
     * Generate mock club rankings for different tiers
     */
    private generateMockClubs(leaderboardType: string): ClubRanking[] {
        const clubNames = [
            'Elite Traders Club',
            'Global Markets Pro',
            'FX Champions',
            'Trading Masters',
            'Market Wizards',
            'Profit Seekers',
            'Bull Traders',
            'Forex Legends',
            'Capital Growth Club',
            'Trading Academy',
            'Market Movers',
            'Professional Traders',
            'Apex Trading Group',
            'Victory Traders',
            'Strategic Investors'
        ]

        const colors = [
            '#FF5733', '#3357FF', '#33FF57', '#FF33F5', '#F5FF33',
            '#33FFF5', '#FF8C33', '#8C33FF', '#33FF8C', '#FF3388',
            '#88FF33', '#3388FF', '#FF5588', '#5588FF', '#88FF55'
        ]

        // Determine number of clubs based on tier (higher tiers have fewer clubs)
        const tierClubCounts = {
            'DIAMOND': 8,
            'GOLD': 10,
            'SILVER': 12,
            'BRONZE': 15
        }

        const clubCount = tierClubCounts[leaderboardType as keyof typeof tierClubCounts] || 10

        // Determine volume range based on tier (higher tiers have more volume)
        const tierVolumeMultipliers = {
            'DIAMOND': { min: 800000, max: 2000000 },
            'GOLD': { min: 400000, max: 1000000 },
            'SILVER': { min: 150000, max: 500000 },
            'BRONZE': { min: 50000, max: 200000 }
        }

        const volumeRange = tierVolumeMultipliers[leaderboardType as keyof typeof tierVolumeMultipliers] || { min: 50000, max: 200000 }

        const clubs: ClubRanking[] = []

        for (let i = 0; i < clubCount; i++) {
            const volume = Math.floor(Math.random() * (volumeRange.max - volumeRange.min) + volumeRange.min)
            const members = Math.floor(Math.random() * 150) + 20

            clubs.push({
                clubId: 1000 + i,
                clubName: clubNames[i % clubNames.length],
                clubImageUrl: `https://ui-avatars.com/api/?name=${encodeURIComponent(clubNames[i % clubNames.length])}&background=${colors[i % colors.length].replace('#', '')}&color=fff&size=128`,
                clubPrimaryColor: colors[i % colors.length],
                totalVolume: volume,
                memberCount: members,
                rank: i + 1
            })
        }

        // Sort by volume descending
        return clubs.sort((a, b) => b.totalVolume - a.totalVolume)
            .map((club, index) => ({ ...club, rank: index + 1 }))
    }
}

export default new LeaguesRepository()
